import { service } from '@ember/service';
import Route from '@ember/routing/route';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type Transition from '@ember/routing/transition';
import type AdminTasApprovalQueueController from 'tio-employee/controllers/authenticated/admin/tas/approval-queue';
import { OpenSearchQueryBuilder, type FilterClause } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import {
  buildApprovalShouldClauses,
  type PendingStatusType,
} from 'tio-common/utils/tuition-assistance/approval-queue-filters';

type Params = {
  search: string;
  order: string;
  approval: PendingStatusType;
  program?: string;
  page: number;
  limit: number;
  disableReviewer?: boolean;
  waitingOnOthers?: boolean;
};

export default class AuthenticatedTasIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  queryParams = {
    search: { refreshModel: true, replace: true },
    order: { refreshModel: true, replace: true },
    approval: { refreshModel: true, replace: true },
    program: { refreshModel: true, replace: true },
    page: { refreshModel: true, replace: true },
    limit: { refreshModel: true, replace: true },
    disableReviewer: { refreshModel: true, replace: true },
    waitingOnOthers: { refreshModel: true, replace: true },
  };

  private resolveSortOrder(order: string) {
    switch (order) {
      case 'last_action_asc':
        return 'tas_application_latest_state_change_at_date.asc,tas_program_instance_latest_state_change_at_date.asc,tas_asset_updated_at_date.asc';
      case 'last_action_desc':
        return 'tas_application_latest_state_change_at_date.desc,tas_program_instance_latest_state_change_at_date.desc,tas_asset_updated_at_date.desc';
      case 'first_submitted_asc':
        return 'attention_priority_date';
      case 'first_submitted_desc':
        return '-attention_priority_date';
      default:
        // Backward compatibility: support old or explicit field-based values from URLs.
        return (
          order ||
          'tas_application_latest_state_change_at_date.asc,tas_program_instance_latest_state_change_at_date.asc,tas_asset_updated_at_date.asc'
        );
    }
  }

  async model(params: Params) {
    const { search, approval, program, page, limit, order, disableReviewer, waitingOnOthers } =
      params;

    const reviewerId = this.sessionContext.user?.employees?.[0]?.id || '-1';

    const builder = new OpenSearchQueryBuilder()
      .addFilter({ term: { company_id: this.sessionContext.currentRole?.company?.id || -1 } })
      .addFilterIf(!disableReviewer && !waitingOnOthers, {
        bool: {
          should: [
            { term: { tas_program_instance_current_reviewers_keyword: reviewerId } },
            { term: { tas_application_current_reviewers_keyword: reviewerId } },
            { term: { tas_approvable_current_reviewers_keyword: reviewerId } },
          ],
          minimum_should_match: 1,
        },
      })
      .addFilterIf(waitingOnOthers, {
        bool: {
          must: [
            // Must be in possible_reviewers (I could potentially review this)
            {
              bool: {
                should: [
                  { term: { tas_program_instance_possible_reviewers_keyword: reviewerId } },
                  { term: { tas_application_possible_reviewers_keyword: reviewerId } },
                  { term: { tas_approvable_possible_reviewers_keyword: reviewerId } },
                ],
                minimum_should_match: 1,
              },
            },
            // Must NOT be in current_reviewers (not my turn right now)
            {
              bool: {
                must_not: [
                  { term: { tas_program_instance_current_reviewers_keyword: reviewerId } },
                  { term: { tas_application_current_reviewers_keyword: reviewerId } },
                  { term: { tas_approvable_current_reviewers_keyword: reviewerId } },
                ],
              },
            },
          ],
        },
      })
      .addFilterIf(program, { term: { tas_program_template_id_keyword: program! } })
      .setSort(this.resolveSortOrder(order), {
        defaultMissing: '_last',
        defaultUnmappedType: 'date',
      })
      .setPagination(page, limit);

    // Sanitize: 'tas_approvable' is not available in tio-employee, treat as 'all'
    const effectiveApproval = approval === 'tas_approvable' ? 'all' : approval || 'all';
    const shouldClauses = buildApprovalShouldClauses(effectiveApproval, {
      includeTasApprovable: false,
    });
    shouldClauses.forEach((clause: FilterClause) => builder.addShould(clause));
    if (shouldClauses.length > 0) {
      builder.setMinimumShouldMatch(1);
    }

    if (search) {
      builder.setSearchTerm(search, ['*'], { fuzziness: 'AUTO' });
    }

    const { content: response } = (await this.store.request(
      executeSearchRequest({
        'model-type': 'TasProgramInstance',
        'search-body': builder.build(),
        ...(search && { 'search-term': search }),
      })
    )) as { content: OpenSearchResponse };

    const responseBuilder = OpenSearchResponseBuilder.from(response);
    return responseBuilder.toPaginatedResult({ includeHighlights: true });
  }

  loading = (transition: Transition, _originRoute: unknown) => {
    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = this.controllerFor(
      'authenticated.admin.tas.approval-queue'
    ) as AdminTasApprovalQueueController;
    controller.loading = true;
    transition.promise?.finally(() => {
      controller.loading = false;
    });
    return true;
  };

  willTransition = (transition: Transition) => {
    const watchedParams = ['approval', 'program', 'search', 'waitingOnOthers'];

    const from = transition.from?.queryParams || {};
    const to = transition.to?.queryParams || {};

    const filterChanged = watchedParams.some((param) => (from[param] || '') !== (to[param] || ''));

    if (filterChanged) {
      // eslint-disable-next-line ember/no-controller-access-in-routes
      const controller = this.controllerFor('authenticated.admin.tas.approval-queue');
      controller.set('page', 1);
    }
    return true;
  };
}
